import { useCallback } from 'react';
import { EdgeTypes, NodeTypes } from 'reactflow';

import { BidirectionalEdgeComponent } from '../components/BidirectionalEdgeComponent';
import { BasicEdgeComponent } from '../components/EdgeComponent';
import * as ErrorNode from '../components/ErrorNodeComponent';
import * as LensNode from '../components/LensNode/LensNode';
import { useInternalDagContext } from '../providers/InternalDagProvider';
import { DbtDagNode } from './DagInterfaces';
import { Grain } from './InternalDagInterfaces';

const _nodesTypes = ['lens', 'error'] as const;

// Multiple DAG components rely on knowledge of all possible node types
export const NODE_TYPES = {
  // Do we need a custom project node instead of modifying the lens node?
  // if we choose the autoload appraoch instead of button approach we don't need either
  lens: LensNode.LensNode,
  error: ErrorNode.ErrorNodeComponent,
} satisfies NodeTypes;

export type NodeType = (typeof _nodesTypes)[number];

type BoundingBox = {
  nodeWidth: number;
  nodeHeight: number;
  layoutWidth: number;
  layoutHeight: number;
};

export type GetBoundingBoxFnInternal<TDbtDagNode extends DbtDagNode> = (
  node: TDbtDagNode,
  activeDbtCloudProject: string | null,
  grain: Grain,
) => BoundingBox;

export type GetBoundingBoxFn<TDbtDagNode extends DbtDagNode> = (
  node: TDbtDagNode,
) => BoundingBox;

const nodeBoundingBoxMap = {
  lens: LensNode.getNodeBoundingBox,
  error: ErrorNode.getNodeBoundingBox,
};

export const useGetNodeBoundingBox = <TDbtDagNode extends DbtDagNode>() => {
  const {
    nodeType: defaultNodeType,
    activeDbtCloudProject,
    grain,
  } = useInternalDagContext();
  return useCallback(
    (node: TDbtDagNode) =>
      nodeBoundingBoxMap[node.type ?? defaultNodeType](
        node,
        activeDbtCloudProject,
        grain,
      ),
    [activeDbtCloudProject, defaultNodeType, grain],
  );
};

export const EDGE_TYPES = {
  basic: BasicEdgeComponent,
  bidirectional: BidirectionalEdgeComponent,
} satisfies EdgeTypes;

export type EdgeType = keyof typeof EDGE_TYPES;
